/**
 * Notification Service
 *
 * Handles all notification CRUD operations and provides helper functions
 * for creating different types of notifications
 */

import { supabase } from './supabaseClient';

// ============================================================================
// NOTIFICATION TYPE DEFINITIONS
// ============================================================================
export const NOTIFICATION_TYPES = {
  GMAIL_CONNECTED: {
    type: 'gmail_connected',
    icon: '📧',
    getTitle: () => 'Gmail Account Connected',
    getMessage: (data) => `${data.email} has been successfully connected`
  },
  GMAIL_REMOVED: {
    type: 'gmail_removed',
    icon: '🗑️',
    getTitle: () => 'Gmail Account Removed',
    getMessage: (data) => `${data.email} and ${data.subscriptionCount || 0} subscriptions removed`
  },
  DATA_RESET: {
    type: 'data_reset',
    icon: '♻️',
    getTitle: () => 'Data Reset Complete',
    getMessage: (data) => {
      const count = data.subscriptionCount || 0;
      return count === 1
        ? 'All subscription data has been cleared. 1 subscription removed.'
        : `All subscription data has been cleared. ${count} subscriptions removed.`;
    }
  },
  PRICE_INCREASE: {
    type: 'price_increase',
    icon: '📈',
    getTitle: () => 'Price Increase Alert',
    getMessage: (data) => `${data.service} increased from $${data.oldPrice} to $${data.newPrice} (${data.changePercentage > 0 ? '+' : ''}${data.changePercentage}%)`
  },
  PRICE_DECREASE: {
    type: 'price_decrease',
    icon: '📉',
    getTitle: () => 'Price Decrease',
    getMessage: (data) => `${data.service} decreased from $${data.oldPrice} to $${data.newPrice} (${data.changePercentage}%)`
  },
  SUBSCRIPTION_ADDED: {
    type: 'subscription_added',
    icon: '➕',
    getTitle: () => 'Subscription Added',
    getMessage: (data) => `${data.service} added at $${data.price}/${data.billingCycle}`
  },
  SUBSCRIPTION_CANCELLED: {
    type: 'subscription_cancelled',
    icon: '❌',
    getTitle: () => 'Subscription Cancelled',
    getMessage: (data) => `${data.service} has been cancelled`
  },
  SYNC_COMPLETED: {
    type: 'sync_completed',
    icon: '✅',
    getTitle: () => 'Gmail Sync Complete',
    getMessage: (data) => `Found ${data.newSubscriptions || 0} new subscriptions from ${data.email}`
  },
  SYNC_FAILED: {
    type: 'sync_failed',
    icon: '❌',
    getTitle: () => 'Gmail Sync Failed',
    getMessage: (data) => `Failed to sync ${data.email}: ${data.error || 'Unknown error'}`
  },
  FEEDBACK_SUBMITTED: {
    type: 'feedback_submitted',
    icon: '💬',
    getTitle: () => 'Thanks for your feedback!',
    getMessage: (data) => `We've received your feedback and will review it soon`
  },
  FEEDBACK_ERROR: {
    type: 'feedback_error',
    icon: '❌',
    getTitle: () => 'Feedback Submission Failed',
    getMessage: (data) => `Failed to submit feedback. Please try again.`
  },
  USERNAME_UPDATED: {
    type: 'username_updated',
    icon: '✏️',
    getTitle: () => 'Username Updated',
    getMessage: (data) => `Your username has been updated to @${data.username}`
  }
};

// ============================================================================
// CORE CRUD OPERATIONS
// ============================================================================

/**
 * Create a new notification
 * @param {string} userId - User ID
 * @param {string} type - Notification type (from NOTIFICATION_TYPES)
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {Object} data - Additional metadata
 * @returns {Promise<Object>} Created notification
 */
export const createNotification = async (userId, type, title, message, data = {}) => {
  try {
    const { data: notification, error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type,
        title,
        message,
        data,
        is_read: false
      })
      .select()
      .single();


    if (error) {
      console.error('💾 [createNotification] Database error:', error);
      throw error;
    }

    return notification;
  } catch (error) {
    console.error('❌ Error creating notification:', error);
    throw error;
  }
};

/**
 * Helper function to create a typed notification
 * @param {string} userId - User ID
 * @param {Object} notificationType - Notification type from NOTIFICATION_TYPES
 * @param {Object} data - Data for message interpolation
 * @returns {Promise<Object>} Created notification
 */
export const createTypedNotification = async (userId, notificationType, data = {}) => {
  const title = notificationType.getTitle(data);
  const message = notificationType.getMessage(data);

  const result = await createNotification(userId, notificationType.type, title, message, data);
  return result;
};

/**
 * Get notifications for a user
 * @param {string} userId - User ID
 * @param {number} limit - Max number of notifications to fetch
 * @param {number} offset - Offset for pagination
 * @param {boolean} unreadOnly - Fetch only unread notifications
 * @returns {Promise<Array>} Array of notifications
 */
export const getNotifications = async (userId, limit = 50, offset = 0, unreadOnly = false) => {
  try {
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (unreadOnly) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} Count of unread notifications
 */
export const getUnreadCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} Updated notification
 */
export const markAsRead = async (notificationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of notifications marked as read
 */
export const markAllAsRead = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false)
      .select();

    if (error) throw error;

    return data?.length || 0;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Delete all notifications for a user
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of notifications deleted
 */
export const deleteAllNotifications = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId)
      .select();

    if (error) throw error;

    return data?.length || 0;
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
};

// ============================================================================
// CONVENIENCE FUNCTIONS FOR COMMON NOTIFICATION TYPES
// ============================================================================

export const notifyGmailConnected = (userId, email) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.GMAIL_CONNECTED, { email });
};

export const notifyGmailRemoved = (userId, email, subscriptionCount) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.GMAIL_REMOVED, { email, subscriptionCount });
};

export const notifyDataReset = (userId, subscriptionCount) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.DATA_RESET, { subscriptionCount });
};

export const notifyPriceIncrease = (userId, service, oldPrice, newPrice, changePercentage, subscriptionId) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.PRICE_INCREASE, {
    service,
    oldPrice,
    newPrice,
    changePercentage,
    subscriptionId
  });
};

export const notifyPriceDecrease = (userId, service, oldPrice, newPrice, changePercentage, subscriptionId) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.PRICE_DECREASE, {
    service,
    oldPrice,
    newPrice,
    changePercentage,
    subscriptionId
  });
};

export const notifySubscriptionAdded = (userId, service, price, billingCycle, subscriptionId) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.SUBSCRIPTION_ADDED, {
    service,
    price,
    billingCycle,
    subscriptionId
  });
};

export const notifySubscriptionCancelled = (userId, service, subscriptionId) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.SUBSCRIPTION_CANCELLED, {
    service,
    subscriptionId
  });
};

export const notifySyncCompleted = (userId, email, newSubscriptions) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.SYNC_COMPLETED, {
    email,
    newSubscriptions
  });
};

export const notifySyncFailed = (userId, email, error) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.SYNC_FAILED, {
    email,
    error
  });
};

export const notifyUsernameUpdated = (userId, username) => {
  return createTypedNotification(userId, NOTIFICATION_TYPES.USERNAME_UPDATED, {
    username
  });
};

export default {
  NOTIFICATION_TYPES,
  createNotification,
  createTypedNotification,
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  deleteAllNotifications,
  // Convenience functions
  notifyGmailConnected,
  notifyGmailRemoved,
  notifyDataReset,
  notifyPriceIncrease,
  notifyPriceDecrease,
  notifySubscriptionAdded,
  notifySubscriptionCancelled,
  notifySyncCompleted,
  notifySyncFailed
};
